import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import { domainOf, outboundHref, slugify } from "../lib/utils-tv";
import { API, recordLaunch } from "../lib/api";
import { fmtList, tiersText, hasFreeTier, keyDifferences, softwareAppLd } from "../lib/compare";

export default function Compare() {
  const { pair } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let alive = true;
    // Deferred to a microtask so setStatus isn't a synchronous state update in
    // the effect body (satisfies react-hooks/set-state-in-effect).
    Promise.resolve().then(() => {
      if (!alive) return;
      setStatus("loading");
      axios
        .get(`${API}/vs/${encodeURIComponent(pair)}`)
        .then((r) => {
          if (!alive) return;
          setData(r.data);
          setStatus("ok");
        })
        .catch((err) => {
          if (!alive) return;
          setStatus(err?.response?.status === 404 ? "notfound" : "error");
        });
    });
    return () => {
      alive = false;
    };
  }, [pair]);

  if (status === "loading") {
    return (
      <Shell testid="compare-loading" path={`/vs/${pair}`} noindex title="Loading…">
        <div className="text-[13px] text-[#888]">
          loading comparison...<span className="tv-blink">_</span>
        </div>
      </Shell>
    );
  }

  if (status === "notfound") {
    return (
      <Shell testid="compare-404" path={`/vs/${pair}`} noindex title="Not found">
        <h1 className="text-xl font-bold border-b border-black pb-2">
          {">"}_ 404 / no such comparison
        </h1>
        <p className="mt-4 text-[13px] text-[#444]">
          One or both tools aren&apos;t in the index.
        </p>
        <p className="mt-4">
          <Link to="/" className="tv-btn">[← back to index]</Link>
        </p>
      </Shell>
    );
  }

  if (status === "error" || !data?.a || !data?.b) {
    return (
      <Shell testid="compare-error" path={`/vs/${pair}`} noindex title="Error">
        <h1 className="text-xl font-bold">{">"}_ error</h1>
        <p className="text-[13px] text-[#444] mt-2">could not load. try again.</p>
      </Shell>
    );
  }

  const { a, b } = data;
  const path = `/vs/${a.slug}-vs-${b.slug}`;
  const seoTitle = `${a.title} vs ${b.title}`;
  const seoDesc = `${a.title} vs ${b.title}: a side-by-side comparison of features, pricing, who each is best for, asset class and fit.`;

  const rows = [
    ["one-liner", a.description, b.description],
    ["best for", a.best_for, b.best_for],
    ["key feature", a.key_feature, b.key_feature],
    ["who uses it", fmtList(a.user_base), fmtList(b.user_base)],
    ["asset class", a.asset_class, b.asset_class],
    [
      "function",
      a.category_2 ? `${a.category}, ${a.category_2}` : a.category,
      b.category_2 ? `${b.category}, ${b.category_2}` : b.category,
    ],
    ["strategies", fmtList(a.strategies), fmtList(b.strategies)],
    ["workflow", fmtList(a.workflow_position), fmtList(b.workflow_position)],
    ["instruments", fmtList(a.instruments), fmtList(b.instruments)],
    ["regions", fmtList(a.regions), fmtList(b.regions)],
    ["pricing", a.pricing || "$?", b.pricing || "$?"],
    ["plans", tiersText(a), tiersText(b)],
    ["free tier", hasFreeTier(a) ? "yes" : "no", hasFreeTier(b) ? "yes" : "no"],
    ["model", a.pricing_model, b.pricing_model],
    ["open source", a.is_open_source ? "yes" : "", b.is_open_source ? "yes" : ""],
    ["via", fmtDelivery(a.delivery), fmtDelivery(b.delivery)],
    ["audience", fmtAudience(a), fmtAudience(b)],
    ["upvotes", String(a.upvotes || 0), String(b.upvotes || 0)],
  ].filter(([, av, bv]) => (av && String(av).trim()) || (bv && String(bv).trim()));

  const differences = keyDifferences(a, b);

  // FAQ schema — answers built strictly from real DB fields (no invention).
  const faqEntries = [
    { q: `What is ${a.title} best for?`, a: a.best_for || a.description },
    { q: `What is ${b.title} best for?`, a: b.best_for || b.description },
    {
      q: `How much do ${a.title} and ${b.title} cost?`,
      a: `${a.title}: ${tiersText(a) || a.pricing || "pricing varies"}. ${b.title}: ${tiersText(b) || b.pricing || "pricing varies"}.`,
    },
    {
      q: `Is ${a.title} or ${b.title} free?`,
      a:
        hasFreeTier(a) || hasFreeTier(b)
          ? [hasFreeTier(a) && a.title, hasFreeTier(b) && b.title].filter(Boolean).join(" and ") +
            (hasFreeTier(a) && hasFreeTier(b) ? " both have a free tier." : " has a free tier.")
          : null,
    },
    {
      q: `What are the main differences between ${a.title} and ${b.title}?`,
      a: differences.length ? differences.join(" ") : null,
    },
  ].filter((f) => f.a);
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntries.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${a.title} vs ${b.title}`,
    numberOfItems: 2,
    itemListElement: [a, b].map((t, i) => ({ "@type": "ListItem", position: i + 1, item: softwareAppLd(t, siteUrl) })),
  };

  return (
    <Shell
      testid="compare-page"
      path={path}
      title={seoTitle}
      description={seoDesc}
      ogType="article"
      jsonLd={[faqLd, itemListLd]}
    >
      <div className="text-[12px] text-[#888] mb-3" data-testid="compare-breadcrumbs">
        <Link to="/" className="tv-link">index</Link>{" / "}
        <span className="text-black">{a.title} vs {b.title}</span>
      </div>

      <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="compare-title">
        {">"}_ {a.title} vs {b.title}
      </h1>

      <p className="mt-4 text-[14px] text-[#222] leading-relaxed" data-testid="compare-intro">
        {a.title} and {b.title}, side-by-side. Compare the facts below, then
        pick the one that fits your workflow.
      </p>

      {/* Comparison table */}
      <div className="mt-6 border border-black" data-testid="compare-table">
        <div className="grid grid-cols-[7rem_1fr_1fr] border-b border-black bg-black text-white text-[13px]">
          <div className="px-2 py-1.5"></div>
          <div className="px-2 py-1.5 font-bold border-l border-[#444]" data-testid="compare-head-a">
            {a.title}
          </div>
          <div className="px-2 py-1.5 font-bold border-l border-[#444]" data-testid="compare-head-b">
            {b.title}
          </div>
        </div>
        {rows.map(([label, av, bv], i) => (
          <div
            key={label}
            className={`grid grid-cols-[7rem_1fr_1fr] text-[13px] ${i % 2 ? "bg-[#FBF8EE]" : "bg-white"}`}
            data-testid={`compare-row-${slugify(label)}`}
          >
            <div className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-[#888] border-t border-[#eee]">
              {label}
            </div>
            <div className="px-2 py-1.5 text-[#000] border-t border-l border-[#eee]">
              {av || "—"}
            </div>
            <div className="px-2 py-1.5 text-[#000] border-t border-l border-[#eee]">
              {bv || "—"}
            </div>
          </div>
        ))}
      </div>

      {/* Key differences — deterministic sentences from stored fields only. */}
      {differences.length > 0 && (
        <section className="mt-8" data-testid="compare-differences">
          <h2 className="text-[15px] font-bold border-b border-[#ddd] pb-1">
            {a.title} vs {b.title}: key differences
          </h2>
          <ul className="mt-2 pl-5 list-disc text-[14px] text-[#222] leading-relaxed space-y-1">
            {differences.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Per-tool overview prose — real, approved copy. Adds genuine textual
          depth (and H2 structure) to an otherwise table-only page. */}
      {(a.overview || b.overview) && (
        <div className="mt-8 space-y-5" data-testid="compare-overviews">
          {a.overview && (
            <section data-testid={`compare-overview-${a.slug}`}>
              <h2 className="text-[15px] font-bold border-b border-[#ddd] pb-1">about {a.title}</h2>
              <p className="mt-2 text-[14px] text-[#222] leading-relaxed">{a.overview}</p>
            </section>
          )}
          {b.overview && (
            <section data-testid={`compare-overview-${b.slug}`}>
              <h2 className="text-[15px] font-bold border-b border-[#ddd] pb-1">about {b.title}</h2>
              <p className="mt-2 text-[14px] text-[#222] leading-relaxed">{b.overview}</p>
            </section>
          )}
        </div>
      )}

      {/* Dual CTAs */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ToolCard tool={a} />
        <ToolCard tool={b} />
      </div>

      {/* Prominent profile anchors — channel internal link equity back up to
          the parent tool profiles. Descriptive anchor text ("<Tool> profile
          page") helps Google attribute these comparison pages to the core
          pages and rescues them from authority dilution. */}
      <div
        className="mt-6 border-t border-[#ddd] pt-4 text-[14px] leading-relaxed text-[#222]"
        data-testid="compare-profile-links"
      >
        <p className="m-0">
          See full data, pricing and alternatives on the{" "}
          <Link to={`/tool/${a.slug}`} className="tv-link font-bold" data-testid="compare-profile-a">
            {a.title} profile page
          </Link>
          {", or the "}
          <Link to={`/tool/${b.slug}`} className="tv-link font-bold" data-testid="compare-profile-b">
            {b.title} profile page
          </Link>
          .
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        <Link to={`/alternatives/${a.slug}`} className="tv-btn" data-testid="compare-alts-a">
          [more {a.title} alternatives]
        </Link>
        <Link to={`/alternatives/${b.slug}`} className="tv-btn" data-testid="compare-alts-b">
          [more {b.title} alternatives]
        </Link>
        <Link to="/" className="tv-btn" data-testid="compare-back-index">
          [← back to index]
        </Link>
      </div>
    </Shell>
  );
}

function ToolCard({ tool }) {
  return (
    <div className="border border-black p-3" data-testid={`compare-card-${tool.slug}`}>
      <Link to={`/tool/${tool.slug}`} className="tv-link font-bold text-[15px]">
        {tool.title}
      </Link>
      <div className="text-[12px] text-[#666] mt-1">{tool.description}</div>
      <a
        href={outboundHref(tool, "compare")}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={() => recordLaunch(tool.id)}
        className="tv-btn tv-btn-primary mt-3 inline-block"
        data-testid={`compare-visit-${tool.slug}`}
      >
        [↗] visit {domainOf(tool.url)}
      </a>
    </div>
  );
}

function fmtDelivery(delivery) {
  if (!Array.isArray(delivery) || delivery.length === 0) return "web";
  return delivery
    .map((d) => {
      if (d === "chatgpt_gpt") return "GPT";
      if (d === "self_host") return "self-host";
      return d.replace(/_/g, " ");
    })
    .join(", ");
}

function fmtAudience(t) {
  if (!t.audience) return "—";
  if (t.audience === "pro" && t.pro_segment) {
    return `pro · ${t.pro_segment.replace("_", " ")}`;
  }
  return t.audience;
}

function Shell({ testid, children, ...seo }) {
  return (
    <div data-testid={testid}>
      <Seo {...seo} />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">{children}</main>
      <Footer />
    </div>
  );
}
